import React, { useCallback, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSelectedElementId } from '@client/store/ui';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { ELEMENT_LIST_WIDTH_CSS } from '@features/proposals/ElementListRow/element-list-column-resize.hook';
import { useElementImports } from './use-element-imports.hook';
import { useImportListKeyboardNavigationContext } from './use-import-list-visible-row-ids.hook';
import {
  isImportListGroupRowId,
  resolveImportListNavSelectionId,
} from './import-list-navigation.helpers';
import {
  useListMultiSelect,
  type SelectRowHandler,
} from './use-list-multi-select.hook';
import { CandidateRowsTable } from './CandidateRowsTable';
import { mergeSx } from '@client/helpers/mui.helpers';

const importTabSurfaceSx = {
  display: 'flex',
  backgroundColor: 'warmSurface.main',
} as const;

const importTabColumnDividerSx = {
  flexShrink: 0,
  width: ELEMENT_LIST_WIDTH_CSS,
  boxShadow: 'inset -1px 0 0 rgba(0,0,0,0.1)',
} as const;

const importTabContentSx = {
  flex: 1,
  minWidth: 0,
} as const;

/** Matches element-list row layout so the left-column divider stays continuous. */
const ImportTabSurface: React.FC<{
  children: React.ReactNode;
  contentSx?: React.ComponentProps<typeof Box>['sx'];
}> = ({ children, contentSx }) => (
  <Box sx={importTabSurfaceSx}>
    <Box sx={importTabColumnDividerSx} />
    <Box sx={mergeSx(importTabContentSx, contentSx)}>{children}</Box>
  </Box>
);

/**
 * Build-view tab listing CSV imports and their candidate rows (DEV-3298).
 * Hydrates the element-imports store for the project on mount.
 */
export const ImportTabPanel: React.FC<{
  projectId: number;
}> = ({ projectId }) => {
  const imports = useElementImports(projectId);
  const selectedElementId = useSelectedElementId();
  const navigateTo = useProjectNavigation();

  const { visibleRowIds, visibleImports, rowsByImport, importedElements } =
    useImportListKeyboardNavigationContext(projectId);

  // Multi-select works in candidate row ids; the URL may hold the *element* id
  // of an already materialized row, so map it back before feeding the hook.
  const activeRowId = resolveImportListNavSelectionId(
    selectedElementId,
    visibleRowIds,
    importedElements,
  );

  const importIdByRowId = useMemo(() => {
    const byRowId = new Map<string, string>();
    for (const [importId, rows] of Object.entries(rowsByImport)) {
      for (const row of rows) byRowId.set(row.id, importId);
    }
    return byRowId;
  }, [rowsByImport]);

  // Import headers are in `visibleRowIds` for keyboard nav but are not
  // selectable rows; dropping them keeps ranges made of candidate rows only.
  const selectableRowIds = useMemo(
    () =>
      visibleRowIds.filter((id) => !isImportListGroupRowId(id, visibleImports)),
    [visibleRowIds, visibleImports],
  );

  const groupIdOf = useCallback(
    (rowId: string) => importIdByRowId.get(rowId),
    [importIdByRowId],
  );

  const { selectedIds, selectRow } = useListMultiSelect(
    selectableRowIds,
    activeRowId,
    groupIdOf,
  );

  const handleSelectRow = useCallback<SelectRowHandler>(
    (id: string, e: React.MouseEvent) => {
      selectRow(id, e);
      void navigateTo({ elementId: id });
    },
    [navigateTo, selectRow],
  );

  if (imports.status === 'pending') {
    return (
      <ImportTabSurface
        contentSx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <CircularProgress size={20} />
      </ImportTabSurface>
    );
  }

  if (imports.status === 'error') {
    return (
      <ImportTabSurface contentSx={{ p: 3 }}>
        <Typography variant="body2" color="error">
          {imports.error.message}
        </Typography>
      </ImportTabSurface>
    );
  }

  const importCount = Object.keys(imports.data.importsById).length;

  if (importCount === 0) {
    return (
      <ImportTabSurface contentSx={{ p: 3 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          No imports yet. Drop a kalkyl CSV or xlsx on the build view to import
          rows.
        </Typography>
      </ImportTabSurface>
    );
  }

  return (
    <CandidateRowsTable
      projectId={projectId}
      selectedRowId={activeRowId ?? null}
      multiSelectedIds={selectedIds}
      onSelectRow={handleSelectRow}
    />
  );
};
